import React, { useMemo, useState } from 'react';

import CommonImage from '@/components/CommonImage';
import { useGetSopListQuery } from '@/features/task-configuration/api/taskConfigurationApi';
import { SOP_LINK_TYPES } from '@/features/task-configuration/constants';

import CreateAndEditSop from './CreateAndEditSop';

const SopAndTutorials = () => {
	const [isCreateSopModalOpen, setIsCreateSopModalOpen] = useState(false);
	const [editingSopId, setEditingSopId] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const { data: sopList = [] } = useGetSopListQuery();

	const filteredSopList = useMemo(() => {
		if (!searchTerm.trim()) return sopList;
		const lowerSearch = searchTerm.toLowerCase().trim();
		return sopList.filter((item) => item?.title?.toLowerCase().includes(lowerSearch));
	}, [sopList, searchTerm]);

	const groupedByStage = useMemo(() => {
		const groups = {};
		filteredSopList.forEach((item) => {
			const stageName = item?.stage?.name || 'Unknown';
			if (!groups[stageName]) {
				groups[stageName] = [];
			}
			groups[stageName].push(item);
		});
		return groups;
	}, [filteredSopList]);

	const stageNames = Object.keys(groupedByStage);
	const totalItems = filteredSopList.length;

	const getHyperlinkAttachments = (attachments = []) => {
		return attachments.filter((att) => att.type === 'HYPERLINK');
	};

	const getLinkTypeLabel = (linkType) => {
		return SOP_LINK_TYPES.find((t) => t.id === linkType)?.value || linkType;
	};

	return (
		<div>
			<div className="task-configuration-header d-flex align-items-center justify-content-between">
				<p className="semibold-14 gray_dark_02">{totalItems} items</p>
				<div className="task-action d-flex align-items-center">
					<div className="form-group size32 m-0 mr-1 search-filter filter-with-animation">
						<div className="with-icon right-icon">
							<input
								type="text"
								placeholder="Search"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
							<span className="right-icon">
								<CommonImage src="/icons/search.png" alt="" />
							</span>
						</div>
					</div>
					<div className="add-new-design">
						<button
							type="button"
							className="button text size32 px-0"
							onClick={() => setIsCreateSopModalOpen(true)}
						>
							<CommonImage src="/icons/add-round.svg" alt="add" />
							<span className="ml-1 gray_dark_02">SOP & Tutorials</span>
						</button>
					</div>
				</div>
			</div>
			<div className="py-2">
				{stageNames.map((stageName) => {
					const items = groupedByStage[stageName];
					return (
						<div
							key={stageName}
							className="journey-configuration-table create-list-table sop-list-table mt-3"
						>
							<p className="semibold-14 mb-12">{stageName}</p>
							<table className="table with-border scroll-x-label">
								<thead>
									<tr>
										<th>SL({items.length})</th>
										<th>Stage name</th>
										<th>SOP name</th>
										<th>Which styles are you seeing here?</th>
										<th>Link</th>
										<th>Usages</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{items.map((sop, index) => {
										const links = getHyperlinkAttachments(sop.attachments);
										return (
											<tr key={sop.id}>
												<td className="sl-column">{index + 1}</td>
												<td className="stage-name-column">{sop.stage?.name}</td>
												<td className="sop-name-column">
													<p>{sop.title}</p>
												</td>
												<td className="which-styles-column">
													<p>{sop.description}</p>
												</td>
												<td className="link-column">
													<div>
														{links.map((link) => (
															<p
																key={link.id}
																className="blue text-underline cursor-pointer mb-2"
															>
																<a
																	href={link.url}
																	target="_blank"
																	rel="noopener noreferrer"
																>
																	{getLinkTypeLabel(link.title)}
																</a>
															</p>
														))}
													</div>
												</td>
												<td className="usages-column">
													<div>-</div>
												</td>
												<td className="action-column">
													<div className="option">
														<div className="dropdown">
															<button
																aria-expanded="false"
																className="btn btn-default dropdown-toggle"
																data-toggle="dropdown"
																id={`menu-${sop.id}`}
																type="button"
															>
																<CommonImage
																	src="/icons/more_icon.svg"
																	alt="more_icon"
																/>
															</button>
															<ul
																aria-labelledby={`menu-${sop.id}`}
																className="dropdown-menu dropdown-menu-right shadow-lg"
																role="menu"
																x-placement="bottom-end"
															>
																<li
																	className="px-4 pb-3 pt-3"
																	role="presentation"
																>
																	<a
																		className="font-weight-normal text-black cursor-pointer"
																		role="menuitem"
																		tabIndex={-1}
																		onClick={() => {
																			setEditingSopId(sop.id);
																			setIsCreateSopModalOpen(true);
																		}}
																	>
																		Edit
																	</a>
																</li>
															</ul>
														</div>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					);
				})}
			</div>
			{isCreateSopModalOpen && (
				<CreateAndEditSop
					sopId={editingSopId}
					onClose={() => {
						setIsCreateSopModalOpen(false);
						setEditingSopId(null);
					}}
				/>
			)}
		</div>
	);
};

export default SopAndTutorials;
